import { useMemo } from 'react';
import SearchableSelect from './SearchableSelect';
import { sameId } from '../ids';

/**
 * A dropdown you type into, with a `<select>`'s API.
 *
 * <p>Every list in the product is searchable now, including the short ones. That is
 * partly consistency — a person should not have to learn which boxes accept typing —
 * and partly that "short" is a property of today's data: the board list is four rows
 * until a customer adds their tenth, and the filter that was fine as a select is the
 * one nobody can use afterwards.
 *
 * <p>{@link SearchableSelect} works in whole options, `{ id, name }`, because a picker
 * has to render what was chosen. A filter works in the scalar it puts in a query string.
 * Translating between the two here is what let two dozen native selects be replaced
 * without two dozen different conversions, each with its own chance of comparing a
 * number to a string and quietly finding nothing.
 */

/**
 * The offerable rows, from options in any of the three shapes the screens hold them in.
 *
 * <p>An option whose value is empty is dropped rather than offered, because "All roles"
 * is what the field says when nothing is chosen — see {@link blankLabel} — not something
 * to choose. Offering it as a row would put a box reading "All roles" next to a list
 * whose first entry is also "All roles", one of which clears the filter and one of which
 * does nothing.
 */
export function toRows(options) {
  return (options ?? [])
    .map((option) =>
      typeof option === 'string'
        ? { id: option, name: option }
        : { id: option.value ?? option.id, name: option.label ?? option.name, hint: option.hint },
    )
    .filter((option) => option.id !== '' && option.id !== null && option.id !== undefined);
}

/**
 * What the box says while nothing is chosen: the caller's placeholder, else the label of
 * the empty-valued option if the list carries one, else a plain invitation to type.
 */
export function blankLabel(options, placeholder) {
  if (placeholder) return placeholder;
  const blank = (options ?? []).find(
    (option) => option != null && typeof option === 'object' && (option.value ?? option.id) === '',
  );
  if (!blank) return 'Type to search';
  return blank.label ?? blank.name ?? 'Type to search';
}

/**
 * The options, plus whatever is already selected if the list does not offer it.
 *
 * <p>For a field that used to be typed and is now picked. A title entered before the
 * list existed can hold "Social Studies" where the Subject Master says "Social
 * Science", and a dropdown that simply cannot represent that value shows an empty box
 * — which saves as a blank and silently loses what somebody actually wrote. Carrying
 * the stray value as an option means the form opens on the truth, and the editor can
 * change it when they mean to rather than by not noticing.
 *
 * <p>Pure, and separate from the component, because the thing it prevents is invisible:
 * nothing errors, a field just empties.
 */
export function withValue(options, value) {
  const current = value === null || value === undefined ? '' : String(value);
  if (!current) return options ?? [];
  const known = toRows(options).some((option) => String(option.id) === current);
  return known ? (options ?? []) : [...(options ?? []), current];
}

/**
 * @param options `{ value, label }`, `{ id, name }`, or plain strings
 * @param value the scalar currently selected, or '' for none
 * @param onChange called with the scalar, never with the option
 */
export default function Dropdown({
  options,
  value,
  onChange,
  placeholder,
  ariaLabel,
  id,
  disabled = false,
  required = false,
  error,
  emptyLabel,
  describedBy,
}) {
  const rows = useMemo(() => toRows(options), [options]);

  const selected = useMemo(
    () => rows.find((option) => sameId(option.id, value)) ?? null,
    [rows, value],
  );

  return (
    <SearchableSelect
      id={id}
      value={selected}
      // Clearing is how a filter goes back to "all"; a required field has no such state.
      onChange={(option) => onChange(option?.id ?? '')}
      options={rows}
      placeholder={blankLabel(options, placeholder)}
      emptyLabel={emptyLabel ?? 'No matches'}
      allowClear={!required}
      disabled={disabled}
      ariaLabel={ariaLabel}
      error={error}
      describedBy={describedBy}
    />
  );
}
